/**
 * ass-renderer.js — window.AssRenderer: ASS/SSA subtitles as their author drew
 * them — positions, fonts, colours, outlines, rotation, fades, karaoke,
 * vector signs — by libass itself (JASSUB 2.5.16, the libass build the web
 * players use; vendored at /vendor/jassub/2.5.16/, MIT + LGPL parts, see the
 * LICENSE there).
 *
 * WHY libass AND NOT A RENDERER OF OUR OWN (measured 23.09.2026 against
 * ffmpeg's `ass` filter — libass — on the same frame, 11 frames of six scripts:
 * dialogue styles, positioned/rotated/blurred signs, karaoke, vector drawing +
 * \clip + opaque box, Russian, Japanese+Korean):
 *
 *                                  pixel IoU     ink within 3 px of the reference
 *   the plain overlay (before)     0.01–0.22     3–48 %
 *   JASSUB, the file's fonts       0.69–0.98     100 % both ways
 *   JASSUB, no fonts in the file   0.29–0.97     86–100 % (the typeface differs,
 *                                                 the layout does not)
 *
 * A renderer of our own can at best approach libass; libass IS the reference,
 * and it is what VLC, mpv, MPC-HC and PotPlayer draw with. Its cost: ~875 KB
 * over the wire (brotli) once, only when an ASS track is chosen, and no work
 * on the main thread — 0 long tasks and no extra dropped frames over a playing
 * 1080p30 video with the heaviest scripts of the set.
 *
 * TWO TRAPS FOUND WHILE MEASURING, both handled here:
 *   · the default font must be PRELOADED. Declared lazily (JASSUB's
 *     `availableFonts` only), it is fetched on first need and the first frames
 *     render nothing — and a PAUSED video never renders again, so subtitles
 *     simply did not appear (IoU 0 on every frame of the set);
 *   · libass does not look for another font that HAS a glyph: a Japanese or
 *     Korean line under a style named «MS Gothic» came out as boxes although a
 *     CJK font was loaded. So a line in a script the default font cannot draw
 *     gets that script's font by name (\fn), but only when the file brings no
 *     fonts of its own — a release that ships fonts has chosen them.
 *
 *   var r = await AssRenderer.create(videoEl, { text, fonts: [Uint8Array…], delay: 0 });
 *   r.setDelay(sec)           // positive = subtitles later
 *   r.setTiming(rate, offset) // drift fix: event times × rate + offset
 *   r.setVisible(on)
 *   r.destroy()
 * create() throws (reason in err.message) when this browser cannot run it;
 * the caller keeps the plain overlay then.
 */
(function () {
'use strict';
if (window.AssRenderer) return;

var BASE = '/vendor/jassub/2.5.16/';
var READY_MS = 20000;

/* Scripts Liberation Sans (the default font) cannot draw, and the font we
   already ship for each (public/vendor/fonts — the same files /auto-captions
   burns captions with). Family names are the ones inside the files. */
var SCRIPT_FONTS = {
    kr: { family: 'Noto Sans KR Thin', url: '/vendor/fonts/NotoSansKR-Regular.ttf' },
    jp: { family: 'Noto Sans JP Thin', url: '/vendor/fonts/NotoSansJP-Regular.ttf' },
    sc: { family: 'Noto Sans SC Thin', url: '/vendor/fonts/NotoSansSC-Regular.ttf' },
    ar: { family: 'IBM Plex Sans Arabic', url: '/vendor/fonts/IBMPlexSansArabic-Regular.ttf' }
};
var RE_KR = /[\uac00-\ud7af\u1100-\u11ff\u3130-\u318f]/;
var RE_KANA = /[\u3040-\u30ff]/;
var RE_HAN = /[\u4e00-\u9fff\u3400-\u4dbf]/;
var RE_AR = /[\u0600-\u06ff\u0750-\u077f\ufb50-\ufdff\ufe70-\ufeff]/;
var RE_DIALOGUE = /^(\s*Dialogue\s*:\s*(?:[^,]*,){9})(.*)$/i;

function scriptOf(plain) {
    if (RE_KR.test(plain)) return 'kr';
    if (RE_KANA.test(plain)) return 'jp';
    if (RE_HAN.test(plain)) return 'sc';
    if (RE_AR.test(plain)) return 'ar';
    return null;
}

/* \fn for lines the default font cannot draw. Returns the new text and the
   fonts it now needs. */
function withScriptFonts(text) {
    var need = {};
    var out = String(text).split(/\r?\n/).map(function (line) {
        var m = line.match(RE_DIALOGUE);
        if (!m) return line;
        var k = scriptOf(m[2].replace(/\{[^}]*\}/g, ''));
        if (!k) return line;
        need[k] = 1;
        return m[1] + '{\\fn' + SCRIPT_FONTS[k].family + '}' + m[2];
    }).join('\n');
    return { text: out, need: Object.keys(need) };
}

/* Event times × rate + offset — the same correction SubtitleParser.retime
   applies to the plain cues, so one drift fix serves both renderers. Times
   INSIDE a line (\t, \move, \fad, \k) are relative to its start and stay. */
function assTime(sec) {
    if (!(sec > 0)) sec = 0;
    var cs = Math.round(sec * 100), h = Math.floor(cs / 360000); cs -= h * 360000;
    var m = Math.floor(cs / 6000); cs -= m * 6000;
    var s = Math.floor(cs / 100); cs -= s * 100;
    return h + ':' + (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s + '.' + (cs < 10 ? '0' : '') + cs;
}
function parseAssTime(t) {
    var m = /^\s*(\d+):(\d{1,2}):(\d{1,2})(?:[.,](\d{1,3}))?\s*$/.exec(t);
    if (!m) return null;
    var frac = m[4] ? parseInt(m[4], 10) / Math.pow(10, m[4].length) : 0;
    return (+m[1]) * 3600 + (+m[2]) * 60 + (+m[3]) + frac;
}
function retimeText(text, rate, offset) {
    if (rate === 1 && !offset) return text;
    return String(text).split(/\r?\n/).map(function (line) {
        var m = /^(\s*Dialogue\s*:\s*[^,]*,)([^,]*),([^,]*)(,.*)$/i.exec(line);
        if (!m) return line;
        var a = parseAssTime(m[2]), b = parseAssTime(m[3]);
        if (a == null || b == null) return line;
        return m[1] + assTime(a * rate + offset) + ',' + assTime(b * rate + offset) + m[4];
    }).join('\n');
}

function support() {
    try {
        return typeof Worker === 'function' && typeof OffscreenCanvas === 'function' &&
            typeof HTMLCanvasElement !== 'undefined' && typeof HTMLCanvasElement.prototype.transferControlToOffscreen === 'function' &&
            typeof WebAssembly === 'object' && typeof ResizeObserver === 'function';
    } catch (_) { return false; }
}

var _mod = null;
function load() {
    if (!_mod) _mod = import(BASE + 'jassub.mjs').then(function (m) { return m.default; }, function (e) { _mod = null; throw e; });
    return _mod;
}

function timeout(p, ms, why) {
    return Promise.race([p, new Promise(function (_, rej) { setTimeout(function () { rej(new Error(why)); }, ms); })]);
}

async function create(video, opts) {
    opts = opts || {};
    if (!support()) throw new Error('unsupported');
    if (!video) throw new Error('no-video');
    var JASSUB = await timeout(load(), READY_MS, 'load-timeout');
    var attached = (opts.fonts || []).filter(function (f) { return f && f.length; });
    var src = String(opts.text || ''), need = [];
    if (!attached.length) { var w = withScriptFonts(src); src = w.text; need = w.need; }
    var rate = 1, offset = 0, delay = +opts.delay || 0;
    var fonts = [BASE + 'default.woff2'].concat(need.map(function (k) { return SCRIPT_FONTS[k].url; }), attached);
    var inst = new JASSUB({
        video: video,
        subContent: retimeText(src, rate, offset),
        workerUrl: BASE + 'jassub-worker.mjs',
        wasmUrl: BASE + 'jassub-worker.wasm',
        modernWasmUrl: BASE + 'jassub-worker-modern.wasm',
        fonts: fonts,
        availableFonts: { 'liberation sans': BASE + 'default.woff2' },
        defaultFont: 'liberation sans',
        queryFonts: false,
        timeOffset: -delay
    });
    /* JASSUB's destroy() awaits `ready` before it terminates the worker — a
       worker that never became ready would live on, so kill it directly. */
    function kill() { try { inst.destroy(); } catch (_) {} try { inst._worker && inst._worker.terminate(); } catch (_) {} }
    try { await timeout(inst.ready, READY_MS, 'ready-timeout'); }
    catch (e) { kill(); throw e; }
    if (!inst.renderer) { kill(); throw new Error('no-renderer'); }
    var visible = true;
    /* JASSUB draws when the video PRESENTS a frame (requestVideoFrameCallback).
       A paused video presents none, so a renderer created — or re-timed —
       while paused drew nothing until the viewer pressed play: the guard
       caught it switching «plain text» back to «as authored» on a paused
       frame (0 ink). So a paused video gets its frame asked for explicitly. */
    function repaint() {
        try {
            if (!video.paused) return;
            if (inst._lastDemandTime) inst.resize(true);
            else inst.manualRender({ mediaTime: video.currentTime, width: video.videoWidth, height: video.videoHeight,
                                     expectedDisplayTime: performance.now() }, true);
        } catch (_) {}
    }
    repaint();
    return {
        scripts: need,
        attachedFonts: attached.length,
        canvas: inst._canvas,
        setDelay: function (sec) { delay = +sec || 0; inst.timeOffset = -delay; repaint(); },
        setTiming: function (r, o) {
            r = (r > 0 && isFinite(r)) ? r : 1; o = isFinite(o) ? +o : 0;
            if (r === rate && o === offset) return;
            rate = r; offset = o;
            try { inst.renderer.setTrack(retimeText(src, rate, offset)); } catch (_) {}
            repaint();
        },
        setVisible: function (on) {
            visible = !!on;
            if (inst._canvas) inst._canvas.style.visibility = visible ? '' : 'hidden';
        },
        isVisible: function () { return visible; },
        destroy: kill
    };
}

window.AssRenderer = {
    support: support,
    create: create,
    _withScriptFonts: withScriptFonts,
    _retimeText: retimeText
};
})();
